/**
 * PKCE auth code parsing and exchange lock (no Supabase imports — testable in isolation).
 * Uses sessionStorage on web; in-memory fallback on native where sessionStorage is absent.
 */

import { OAUTH_PKCE_EXCHANGE_LOCK_STORAGE_KEY } from '../constants/oauth.constants'

/** Native / no sessionStorage: prevents double exchange across WebBrowser + deep-link callback. */
let memoryPkceExchangeLockCode: string | null = null

export function extractOAuthAuthCodeFromCallbackUrl(callbackUrl: string): string | null {
  if (!callbackUrl?.trim()) return null
  try {
    const url = new URL(callbackUrl)
    const fromQuery = url.searchParams.get('code')
    if (fromQuery) return fromQuery
    const hash = url.hash.replace(/^#/, '')
    if (!hash) return null
    return new URLSearchParams(hash).get('code')
  } catch {
    const queryMatch = callbackUrl.match(/[?&]code=([^&]+)/)
    if (queryMatch?.[1]) {
      try {
        return decodeURIComponent(queryMatch[1])
      } catch {
        return queryMatch[1]
      }
    }
    return null
  }
}

export function claimOAuthPkceExchangeLock(authCode: string): boolean {
  if (memoryPkceExchangeLockCode === authCode) return false

  if (typeof sessionStorage !== 'undefined') {
    try {
      const existing = sessionStorage.getItem(OAUTH_PKCE_EXCHANGE_LOCK_STORAGE_KEY)
      if (existing === authCode) return false
      sessionStorage.setItem(OAUTH_PKCE_EXCHANGE_LOCK_STORAGE_KEY, authCode)
    } catch {
      // fall through to memory lock
    }
  }

  memoryPkceExchangeLockCode = authCode
  return true
}

export function releaseOAuthPkceExchangeLock(): void {
  memoryPkceExchangeLockCode = null
  if (typeof sessionStorage === 'undefined') return
  try {
    sessionStorage.removeItem(OAUTH_PKCE_EXCHANGE_LOCK_STORAGE_KEY)
  } catch {
    // ignore storage errors
  }
}

/** Test-only reset for in-memory lock between cases. */
export function resetOAuthPkceExchangeLockForTests(): void {
  memoryPkceExchangeLockCode = null
}
